// One Google Places address box, reusable anywhere an address is typed.
// Start typing, pick a suggestion, and the full address drops in — same
// lookup the deal and contact forms already use. Falls back to a plain text
// input if the Maps script can't load, so an address is always enterable.
import { useRef } from "react";
import { Autocomplete, useLoadScript } from "@react-google-maps/api";

// Same key + libraries as everywhere else — the Maps loader requires
// identical options across the app or it refuses to re-initialize.
const GMAPS_LIBRARIES = ["places"];
const GMAPS_KEY = "AIzaSyClMaFJLo3buNYK-siytTa6AWD_72TgrE8";

// Splits a Google place into the parts most of our forms want.
export function partsFromPlace(place) {
  const comps = place?.address_components || [];
  const get = (type, short = false) => {
    const c = comps.find((x) => x.types.includes(type));
    return c ? (short ? c.short_name : c.long_name) : "";
  };
  const street = [get("street_number"), get("route")].filter(Boolean).join(" ");
  return {
    street: street || place?.formatted_address || "",
    city: get("locality") || get("sublocality") || "",
    state: get("administrative_area_level_1", true) || "",
    postalCode: get("postal_code") || "",
    country: get("country") || "",
    formatted: place?.formatted_address || street || "",
  };
}

export default function AddressAutocomplete({
  value,
  onChange,
  // Called with the split parts when a suggestion is picked. Forms that
  // only store one address string can ignore it and use onChange.
  onPlaceParts,
  placeholder = "Start typing an address…",
  className = "",
  autoFocus = false,
  onBlur,
  onKeyDown,
  disabled = false,
}) {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: GMAPS_KEY,
    libraries: GMAPS_LIBRARIES,
  });
  const acRef = useRef(null);

  const input = (
    <input
      type="text"
      value={value || ""}
      onChange={(e) => onChange?.(e.target.value)}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      autoFocus={autoFocus}
      disabled={disabled}
      className={className}
    />
  );

  // Maps not ready (still loading, offline, or key trouble): the plain input
  // still types and saves — autocomplete is an assist, never a gate.
  if (!isLoaded) return input;

  return (
    <Autocomplete
      onLoad={(ac) => {
        acRef.current = ac;
      }}
      onPlaceChanged={() => {
        const place = acRef.current?.getPlace();
        if (!place) return;
        const parts = partsFromPlace(place);
        onChange?.(parts.formatted);
        onPlaceParts?.(parts);
      }}
      options={{ componentRestrictions: { country: "us" } }}
    >
      {input}
    </Autocomplete>
  );
}
